import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wrench, CheckCircle2 } from "lucide-react";
import Reveal, { Overline } from "@/components/Reveal";
import { MODELS, TIME_SLOTS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TYPES = [
  { id: "periodic", label: "Periodic Maintenance" },
  { id: "repair", label: "Running Repair" },
  { id: "body-paint", label: "Body & Paint" },
  { id: "accessories", label: "Genuine Accessories" },
  { id: "other", label: "Other" },
];

const initialForm = {
  name: "", phone: "", email: "", model: "", regNumber: "", serviceType: "",
  date: "", slot: "", pickupDrop: false, address: "",
};

export default function ServiceBooking() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const today = new Date().toISOString().split("T")[0];

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit =
    form.name.trim().length >= 2 &&
    /^[0-9+\s-]{10,15}$/.test(form.phone.trim()) &&
    form.model &&
    form.serviceType &&
    form.date &&
    form.slot &&
    (!form.pickupDrop || form.address.trim().length >= 8);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/service-booking`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        model: form.model,
        email: form.email.trim() || null,
        reg_number: form.regNumber.trim() || null,
        service_type: SERVICE_TYPES.find((s) => s.id === form.serviceType)?.label || form.serviceType,
        preferred_date: form.date,
        time_slot: form.slot,
        pickup_drop: form.pickupDrop,
        address: form.pickupDrop ? form.address.trim() : null,
      });
      setDone(data);
      toast.success("Service booked! Our service team will call you to confirm.");
    } catch {
      toast.error("Could not submit. Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="service" data-testid="service-section" className="relative bg-[#05141F] py-24 sm:py-32 overflow-hidden">
      <div className="absolute -right-40 top-1/4 w-[500px] h-[500px] rounded-full bg-[#FFFFFF]/8 blur-[160px] pointer-events-none" />
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center">
          <Overline className="justify-center flex">Kia Owners</Overline>
          <h2 className="mt-4 font-display font-extrabold tracking-tight leading-[1.02] text-3xl sm:text-5xl text-white">
            Book a <span className="text-[#FFFFFF]">Service.</span>
          </h2>
          <p className="mt-5 text-slate-400 max-w-lg mx-auto">
            Factory-trained technicians, genuine parts — with optional pickup &amp; drop across Jajpur.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 bg-[#0D1822] border border-white/10 clip-card p-6 sm:p-10">
            {done ? (
              <div data-testid="service-confirmation" className="text-center py-10">
                <CheckCircle2 size={56} className="mx-auto text-[#FFFFFF]" />
                <h3 className="mt-6 font-display font-extrabold text-2xl sm:text-3xl text-white">Service Booked.</h3>
                <p className="mt-4 text-slate-400 max-w-md mx-auto leading-relaxed">
                  {done.name}, your {done.service_type} for the {done.model} is requested for{" "}
                  <span className="text-white">{done.preferred_date}</span> at{" "}
                  <span className="text-white">{done.time_slot}</span>
                  {done.pickup_drop ? " with pickup & drop" : ""}. Reference:{" "}
                  <span className="font-mono text-[#FFFFFF]">{done.id.slice(0, 8).toUpperCase()}</span>
                </p>
                <button
                  data-testid="service-book-another-button"
                  onClick={() => { setForm(initialForm); setDone(null); }}
                  className="mt-8 border border-white/20 hover:border-[#FFFFFF] text-white text-xs font-semibold uppercase tracking-[0.18em] px-8 py-3.5 transition-colors duration-300"
                >
                  Book Another
                </button>
              </div>
            ) : (
              <div className="space-y-7">
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-4">What does your Kia need?</p>
                  <div className="flex flex-wrap gap-2">
                    {SERVICE_TYPES.map((s) => (
                      <button
                        key={s.id}
                        data-testid={`service-type-${s.id}`}
                        onClick={() => set("serviceType", s.id)}
                        className={`font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2.5 border transition-colors duration-300 ${
                          form.serviceType === s.id
                            ? "bg-[#FFFFFF] border-[#FFFFFF] text-black"
                            : "border-white/15 text-slate-400 hover:border-white/40 hover:text-white"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2.5">Full Name *</label>
                    <input data-testid="service-name-input" type="text" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className="kia-input w-full" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2.5">Mobile Number *</label>
                    <input data-testid="service-phone-input" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 98XXX XXXXX" className="kia-input w-full" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2.5">Email (optional — get a confirmation)</label>
                    <input data-testid="service-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className="kia-input w-full" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2.5">Your Kia Model *</label>
                    <select data-testid="service-model-select" value={form.model} onChange={(e) => set("model", e.target.value)} className="kia-input w-full">
                      <option value="" className="bg-[#0D1822]">Select model</option>
                      {MODELS.map((m) => (
                        <option key={m.id} value={m.name} className="bg-[#0D1822]">{m.name}</option>
                      ))}
                      <option value="Other / Older Kia" className="bg-[#0D1822]">Other / Older Kia</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2.5">Registration No. (optional)</label>
                    <input data-testid="service-reg-input" type="text" value={form.regNumber} onChange={(e) => set("regNumber", e.target.value)} placeholder="OD-04-XX-0000" className="kia-input w-full" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2.5">Preferred Date *</label>
                    <input data-testid="service-date-input" type="date" min={today} value={form.date} onChange={(e) => set("date", e.target.value)} className="kia-input w-full" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2.5">Time Slot *</label>
                    <div className="flex flex-wrap gap-2">
                      {TIME_SLOTS.map((s) => (
                        <button
                          key={s}
                          data-testid={`service-slot-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                          onClick={() => set("slot", s)}
                          className={`font-mono text-xs px-3 py-2 border transition-colors duration-300 ${
                            form.slot === s ? "bg-[#FFFFFF] border-[#FFFFFF] text-black" : "border-white/15 text-slate-400 hover:border-white/40"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-4">Pickup &amp; Drop?</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[
                      { v: false, label: "I'll Drive In", sub: "NH-20 Panikoili Service Center" },
                      { v: true, label: "Pick Up My Car", sub: "Free within Jajpur city limits" },
                    ].map((o) => (
                      <button
                        key={String(o.v)}
                        data-testid={o.v ? "service-pickup-yes" : "service-pickup-no"}
                        onClick={() => set("pickupDrop", o.v)}
                        className={`flex items-center gap-4 p-5 border text-left transition-colors duration-300 ${
                          form.pickupDrop === o.v ? "border-[#FFFFFF] bg-[#FFFFFF]/10" : "border-white/10 hover:border-white/30"
                        }`}
                      >
                        <Wrench size={20} className={form.pickupDrop === o.v ? "text-[#FFFFFF]" : "text-slate-500"} />
                        <span>
                          <span className="block font-display font-bold text-white text-sm">{o.label}</span>
                          <span className="block text-xs text-slate-500 mt-0.5">{o.sub}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                  {form.pickupDrop && (
                    <div className="mt-5">
                      <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 block mb-2.5">Pickup Address *</label>
                      <textarea
                        data-testid="service-address-input"
                        value={form.address}
                        onChange={(e) => set("address", e.target.value)}
                        placeholder="Full address with landmark"
                        rows={3}
                        className="kia-input w-full resize-none"
                      />
                    </div>
                  )}
                </div>

                <div className="border-t border-white/10 pt-7 flex justify-end">
                  <button
                    data-testid="service-submit-button"
                    onClick={submit}
                    disabled={!canSubmit || submitting}
                    className="inline-flex items-center gap-2 bg-[#FFFFFF] hover:bg-[#D4D4D4] disabled:opacity-40 disabled:pointer-events-none text-black text-xs font-semibold uppercase tracking-[0.18em] px-8 py-4 clip-notch transition-colors duration-300"
                  >
                    {submitting ? "Booking..." : "Confirm Service Booking"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
